import { AdvancedTable } from "@woodmac-hycb/advanced-table";
import { Button } from "@woodmac-hycb/button";
import { Input } from "@woodmac-hycb/form-input";
import { Icon24Trash } from "@woodmac-hycb/icon24";
import { Col, View } from "@woodmac-hycb/layout";
import { Text } from "@woodmac-hycb/text";
import { useEffect, useRef, useState } from "react";
import { getAlertsForEntity, deleteAlert, updateAlertActive } from "../services/AlertService";
import './CustomAlertManager.css';
import { useToggleRegion } from '../hooks/useToggleRegion';
import { Modal } from "@woodmac-hycb/modal";

function PlantAlertsList({ selectedPlant, setPlottedAlerts, alertToAdd, setSelectedAlert }) {

    const [alerts, setAlerts] = useState([]);
    const [alertIdsToPlot, setAlertIdsToPlot] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const gridRef = useRef(null);


    const { isRegionNa } = useToggleRegion();
    /*const { getAccessTokenSilently } = authClient();*/

    useEffect(() => {
        const alertsToPlot = alerts.filter(a => alertIdsToPlot.includes(a.id));
        setPlottedAlerts(alertsToPlot);
    }, [alertIdsToPlot, setPlottedAlerts, alerts]);

    useEffect(() => {
        const getAlerts = async () => {
            setLoading(true);
            /*const token = await getAccessTokenSilently();*/
            const response = await getAlertsForEntity(selectedPlant.entityId, isRegionNa ? "na" : "eu", "");
            if (response.status === 200) {
                const newAlerts = response.data.sort((a, b) => a.title.localeCompare(b.title));
                setAlerts(newAlerts);
                setLoading(false);
            }
        }

        if (selectedPlant) {
            getAlerts();
        } else {
            setAlerts([]);
        }

        setAlertIdsToPlot([]);
    }, [selectedPlant, isRegionNa])

    useEffect(() => {
        if (alertToAdd) {
            if (alerts.find((a) => (a.id === alertToAdd.id))) {
                setAlerts((alerts) => alerts.map((a) => {
                    if (a.id === alertToAdd.id) {
                        return alertToAdd;
                    } else {
                        return a;
                    }
                }));
            } else {
                setAlerts((alerts) => [...alerts, alertToAdd]);
            }
        }
    }, [alertToAdd])


    const handleDelete = async (alert) => {
        /*const token = await getAccessTokenSilently();*/
        deleteAlert(alert.id, isRegionNa ? "na" : "eu", "")
            .then(() => {
                setAlerts(alerts.filter((a) => a.id !== alert.id))
                clearSelectedAlert();

            })
            .catch((error) => {
                console.error("Error:", error);
            })
    }

    const clearSelectedAlert = () => {
        setSelectedAlert(null);
        gridRef.current.deselectAll();
    }

    const handlePlotOnChange = (alert) => {
        const includesAlert = alertIdsToPlot.some(a => a === alert.id);
        const newIds = includesAlert ? alertIdsToPlot.filter((a) => a !== alert.id) : [...alertIdsToPlot, alert.id];
        setAlertIdsToPlot(newIds);
    }

    const handleActiveOnChange = async (alert) => {
        const alertActive = alert.isActive;
        /*const token = await getAccessTokenSilently();*/
        const response = await updateAlertActive(alert.id, { ...alert, isActive: !alertActive }, isRegionNa ? "na" : "eu", "");
        if (response.status === 200) {
            setAlerts(prevAlerts => prevAlerts.map((a) => a.id === alert.id ? { ...a, isActive: !alertActive } : a));
        }
    }

    function ActiveRenderer(e) {
        const alert = e.data;
        return (
            <Input type='checkbox' checked={alert.isActive} onChange={() => handleActiveOnChange(alert)} />
        )
    }

    function PlotRenderer(e) {
        const alert = e.data;
        return (
            <Input type='checkbox' checked={alertIdsToPlot.some(a => a === alert.id)} onChange={() => handlePlotOnChange(alert)} />

        )
    }

    function DeleteRenderer(e) {
        const alert = e.data;
        const handleConfirmDelete = async () => {
            setDeleteModalOpen(false);
            await handleDelete(alert);
        }
        return (
            <div>
                <Icon24Trash style={{ cursor: 'pointer' }} onClick={() => setDeleteModalOpen(true)}></Icon24Trash>
                <Modal
                    open={deleteModalOpen}
                    dismissable
                    onCancel={() => setDeleteModalOpen(false)}
                    title="Delete Alert"
                    primaryAction={<Button negative onClick={() => handleConfirmDelete()}>Delete</Button>}
                >
                    <Text>Are you sure you want to delete {alert.title}?</Text>
                </Modal>
            </div>
        )
    }

    const generateRows = () => {
        return alerts.map((a) => (
            { ...a, type: a.typeId === 200 ? 'Output Change' : 'Boundary' }
        ));
    }

    const onGridReady = (params) => {
        const { api } = params;
        gridRef.current = api;

        // Call a function to adjust the column widths on initial load
        adjustColumnWidths();
    };

    const adjustColumnWidths = () => {
        if (gridRef.current) {
            gridRef.current.sizeColumnsToFit(); // Resize columns to fit the available space
        }
    };

    const onRowSelectionChanged = (e) => {
        const isSelected = e.node.isSelected();
        if (isSelected) {
            const alert = e.data;
            setSelectedAlert(alert)
        }
    }

    return (
        <Col gap={16} flex=".75">
            <Text fontSize="ty120">Entity Alerts</Text>
            <View height={380}>
                {isLoading ?
                    <AdvancedTable loading
                        pagination={false} />
                    :
                    <AdvancedTable
                        pagination={false}
                        suppressMovableColumns={true}
                        columnDefs={[
                            { headerName: "Alert", field: "title", flex: 1 },
                            { headerName: "Alert Type", field: "type", flex: 1 },
                            { headerName: "Active", field: "active", flex: 1, cellRenderer: ActiveRenderer },
                            { headerName: "Plot", field: "plot", flex: 1, cellRenderer: PlotRenderer },
                            { headerName: "Delete", field: "delete", flex: 1, cellRenderer: DeleteRenderer }
                        ]}
                        domLayout="normal"
                        suppressRowClickSelection={false}
                        rowData={generateRows()}
                        rowSelection="single"
                        onRowSelected={onRowSelectionChanged}
                        suppressScrollOnNewData={true}
                        onGridReady={onGridReady}
                    />
                }
            </View >
        </Col >
    );
}

export default PlantAlertsList;